import { AQLogger } from "aq-logger";
import { PitangoMarketCommon } from "pitango-market-common";
import { CategoriesApi, ProductsApi } from "../../../generated/api-client";
import { generatedConfiguration, ResponseError } from "@/services/api/generated-client";
import type { LocaleCode } from "@/i18n/locales";
import type { Product } from "@/data/products";
import type { Category } from "@/data/categories";
import { PLACEHOLDER_IMAGE_URL } from "@/utils/assets";
import type { DTOProductPromotionView as GeneratedProductPromotionView, DTOProductView as GeneratedProductView } from "../../../generated/api-client";
import { GENERAL_USER_ERROR_MESSAGE } from "@/services/api/service-errors";

import Products = PitangoMarketCommon.WebAPI.Products;
import Categories = PitangoMarketCommon.WebAPI.Categories;
import DTO = PitangoMarketCommon.DTO;

const logger = new AQLogger("CatalogApi");
const productsClient = new ProductsApi(generatedConfiguration);
const categoriesClient = new CategoriesApi(generatedConfiguration);

type CatalogProductView = GeneratedProductView;

// Product/category images are served directly from S3 (same bucket the
// legacy client reads from), not proxied through the API - matches
// `client/src/app-global/constants.tsx`'s PRODUCT_IMAGE/CATEGORY_IMAGE.
const S3_BUCKET_URI = import.meta.env.VITE_S3_BUCKET_URI;

export function productImageUrl(imageFile: string | null | undefined): string {
    return imageFile ? `${S3_BUCKET_URI}/product-images/${imageFile}` : PLACEHOLDER_IMAGE_URL;
}

function categoryImageUrl(imageFile: string | null | undefined): string {
    return imageFile ? `${S3_BUCKET_URI}/category-images/${imageFile}` : PLACEHOLDER_IMAGE_URL;
}

function toDate(value: string | null | undefined): Date | null | undefined {
    return value == null ? value : new Date(value);
}

function toPromotion(view: GeneratedProductPromotionView): DTO.ProductPromotionView {
    return {
        ...view,
        fromDate: toDate(view.fromDate),
        toDate: toDate(view.toDate)
    };
}

function toProduct(view: CatalogProductView): Product {
    return {
        id: view.productId,
        slugUrl: view.slugUrl ?? null,
        image: productImageUrl(view.imageFile),
        // `promotionPrice` is a legacy scalar and cannot represent quantity tiers.
        price: view.onlinePrice && view.onlinePrice > 0 ? view.onlinePrice : null,
        promotions: view.promotions?.map(toPromotion) ?? [],
        brand: undefined,
        categoryId: view.categories?.[0]?.categoryId,
        categories: (view.categories ?? []).map(category => ({ id: category.categoryId, name: category.categoryName })),
        bestSellerRank: view.rank,
        name: view.productName,
        description: view.shortDesc ?? ""
    };
}

function toCategory(view: DTO.CategoryView): Category {
    return {
        id: view.categoryId,
        icon: "🛒",
        count: Number(view.totalProducts ?? 0),
        name: view.categoryName,
        image: categoryImageUrl(view.imageFile),
        breadcrumbs: view.breadcrumbs?.map(item => ({ id: item.categoryId, name: item.categoryName })) ?? undefined,
        children: view.children?.map(toCategory) ?? undefined
    };
}

/**
 * Catalog server-data boundary: real HTTP calls against the public
 * products/categories API (see `server/src/controllers/public-api/`).
 * Every method is locale-aware because the server localizes
 * `productName`/`shortDesc`/`categoryName` per request rather than shipping
 * every language at once.
 */
export const catalogApi = {
    async listCategories(locale: LocaleCode): Promise<Category[]> {
        try {
            logger.debug("Listing categories...");
            const response = await categoriesClient.categoriesGetCategories({ languageCode: locale });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to list categories.", "categories list rejected");
            }
            logger.success("Categories listed.");
            return response.data.categories.map(toCategory);
        } catch (error) {
            throw logger.exception("Failed to list categories.", error);
        }
    },

    async getCategory(locale: LocaleCode, categoryId: string): Promise<Category | null> {
        try {
            logger.debug("Fetching category...");
            const response = await categoriesClient.categoriesGetCategory({ languageCode: locale, categoryId });
            if (response.reject || !response.data) return null;
            logger.success("Category fetched.");
            return toCategory(response.data.category);
        } catch (error) {
            if (error instanceof ResponseError && error.response.status === 404) return null;
            throw logger.exception("Failed to fetch category.", error);
        }
    },

    async listProducts(locale: LocaleCode, categoryId: string, pageIndex = 0): Promise<{ products: Product[]; total: number; pageCount: number }> {
        try {
            logger.debug("Listing products...");
            const response = await productsClient.productsGetProducts({ languageCode: locale, categoryId, pageIndex });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to list products.", "products list rejected");
            }
            logger.success("Products listed.");
            return {
                products: response.data.products.map(toProduct),
                total: response.data.total,
                pageCount: Math.ceil(response.data.total / Products.PAGE_SIZE)
            };
        } catch (error) {
            throw logger.exception("Failed to list products.", error);
        }
    },

    async searchProducts(searchTerm: string): Promise<Product[]> {
        try {
            if (!searchTerm.trim()) return [];
            logger.debug("Searching products...");
            const response = await productsClient.productsSearchProducts({ searchTerm });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to search products.", "product search rejected");
            }
            logger.success("Products searched.");
            return response.data.products.map(toProduct);
        } catch (error) {
            throw logger.exception("Failed to search products.", error);
        }
    },

    async getProductDetails(locale: LocaleCode, productRef: string): Promise<Product | null> {
        try {
            logger.debug("Fetching product details...");
            const response = await productsClient.productsGetProductDetails({ languageCode: locale, productRef });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to fetch product details.", "product details rejected");
            }
            logger.success("Product details fetched.");
            return toProduct(response.data.product);
        } catch (error) {
            if (error instanceof ResponseError && error.response.status === 404) return null;
            throw logger.exception("Failed to fetch product details.", error);
        }
    },

    async listTopProducts(locale: LocaleCode, limit = 10): Promise<Product[]> {
        try {
            logger.debug("Listing top products...");
            const response = await productsClient.productsGetTopProducts({ languageCode: locale, limit });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to list top products.", "top products list rejected");
            }
            logger.success("Top products listed.");
            return response.data.products.map(toProduct);
        } catch (error) {
            throw logger.exception("Failed to list top products.", error);
        }
    },

    async listPromotedProducts(locale: LocaleCode, pageIndex = 0): Promise<{ products: Product[]; total: number; pageCount: number }> {
        try {
            logger.debug("Listing promoted products...");
            const response = await productsClient.productsGetPromotedProducts({ languageCode: locale, pageIndex });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to list promoted products.", "promoted products list rejected");
            }
            logger.success("Promoted products listed.");
            return {
                products: response.data.products.map(toProduct),
                total: response.data.total,
                pageCount: Math.ceil(response.data.total / Products.PAGE_SIZE)
            };
        } catch (error) {
            throw logger.exception("Failed to list promoted products.", error);
        }
    }
};
